import React, { useState } from 'react';
import {
  Activity, Search, Calendar, MapPin, Hash, User,
  StopCircle, CheckCircle2, X,
  Wind, Bed, Stethoscope, Package, History, Info
} from 'lucide-react';
import {
  locationsActives as locationsActivesInit,
  locationsTerminees,
  formatDate,
  getStatutLabel, getStatutColor
} from '../data/mockData';

const iconForRef = (code) => {
  if (code.startsWith('OXY')) return Wind;
  if (code.startsWith('CPAP')) return Bed;
  if (code.startsWith('VNI')) return Activity;
  if (code.startsWith('ASP')) return Stethoscope;
  return Package;
};

const Locations = () => {
  const [activeTab, setActiveTab] = useState('actives');
  const [searchTerm, setSearchTerm] = useState('');
  const [filtreStatut, setFiltreStatut] = useState('tous');
  const [stopModalLoc, setStopModalLoc] = useState(null);
  const [motifArret, setMotifArret] = useState('');
  const [dateArret, setDateArret] = useState(new Date().toISOString().split('T')[0]);
  const [locationsArretees, setLocationsArretees] = useState([]);
  const [confirmation, setConfirmation] = useState(null);

  const locationsActives = locationsActivesInit.filter(l => !locationsArretees.includes(l.id));

  const filtreLocations = (locations) => {
    let result = locations;
    if (searchTerm) {
      const t = searchTerm.toLowerCase();
      result = result.filter(l =>
        l.id.toLowerCase().includes(t) ||
        l.materielNom.toLowerCase().includes(t) ||
        l.patientReference.toLowerCase().includes(t) ||
        l.service.toLowerCase().includes(t)
      );
    }
    if (filtreStatut !== 'tous' && activeTab === 'actives') {
      result = result.filter(l => l.statut === filtreStatut);
    }
    return result;
  };

  const locationsAffichees = activeTab === 'actives'
    ? filtreLocations(locationsActives)
    : filtreLocations(locationsTerminees);

  const ouvrirArret = (loc) => {
    setStopModalLoc(loc);
    setMotifArret('');
    setDateArret(new Date().toISOString().split('T')[0]);
  };

  const confirmerArret = () => {
    setLocationsArretees(prev => [...prev, stopModalLoc.id]);
    setConfirmation({
      message: `La location ${stopModalLoc.id} a été arrêtée. Le matériel sera repris par notre technicien.`,
      type: 'success',
    });
    setStopModalLoc(null);
    setTimeout(() => setConfirmation(null), 5000);
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1 className="page-title">Mes locations</h1>
          <p className="page-subtitle">Suivi de votre parc de matériel en location et gestion des arrêts</p>
        </div>
      </div>

      {/* Confirmation d'arrêt */}
      {confirmation && (
        <div className="alert-banner alert-success">
          <div className="alert-banner-icon"><CheckCircle2 size={24} /></div>
          <div className="alert-banner-content">
            <h3>Location arrêtée avec succès</h3>
            <p>{confirmation.message}</p>
          </div>
          <button className="btn-icon-sm" onClick={() => setConfirmation(null)}><X size={16} /></button>
        </div>
      )}

      {/* Onglets */}
      <div className="doc-tabs">
        <button
          className={`doc-tab ${activeTab === 'actives' ? 'active' : ''}`}
          onClick={() => setActiveTab('actives')}
        >
          <Activity size={18} />
          <span>Locations actives</span>
          <span className="doc-tab-count">{locationsActives.length}</span>
        </button>
        <button
          className={`doc-tab ${activeTab === 'historique' ? 'active' : ''}`}
          onClick={() => setActiveTab('historique')}
        >
          <History size={18} />
          <span>Historique</span>
          <span className="doc-tab-count">{locationsTerminees.length + locationsArretees.length}</span>
        </button>
      </div>

      {/* Toolbar */}
      <div className="doc-toolbar">
        <div className="doc-search">
          <Search size={18} className="search-icon" />
          <input
            type="text"
            placeholder="Rechercher par n°, matériel, patient ou service..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button className="search-clear" onClick={() => setSearchTerm('')}>
              <X size={16} />
            </button>
          )}
        </div>
        {activeTab === 'actives' && (
          <div className="doc-toolbar-right">
            <div className="doc-filter-group">
              <label>Statut :</label>
              <select value={filtreStatut} onChange={(e) => setFiltreStatut(e.target.value)}>
                <option value="tous">Tous</option>
                <option value="active">Active</option>
                <option value="a_renouveler">À renouveler</option>
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Liste des locations */}
      {locationsAffichees.length === 0 ? (
        <div className="empty-state">
          <Activity size={48} strokeWidth={1} />
          <h3>Aucune location {activeTab === 'actives' ? 'active' : 'dans l\'historique'}</h3>
          <p>{activeTab === 'actives' ? 'Vous n\'avez actuellement aucun matériel en location.' : 'Aucune location terminée pour le moment.'}</p>
        </div>
      ) : (
        <div className="locations-list">
          {locationsAffichees.map((loc) => {
            const Icon = iconForRef(loc.materielCode);
            const statutStyle = getStatutColor(loc.statut);
            const isActive = activeTab === 'actives';

            return (
              <div key={loc.id} className={`location-card ${loc.statut === 'a_renouveler' ? 'location-card-warning' : ''}`}>
                <div className="location-card-icon">
                  <Icon size={32} />
                </div>

                <div className="location-card-main">
                  <div className="location-card-header">
                    <div>
                      <h3 className="location-card-title">{loc.materielNom}</h3>
                      <p className="location-card-meta">
                        <Hash size={12} /> {loc.id} · N° série <span className="mono-text">{loc.numeroSerie}</span>
                      </p>
                    </div>
                    <span
                      className="doc-status-badge"
                      style={{ background: statutStyle.bg, color: statutStyle.color, borderColor: statutStyle.border }}
                    >
                      {getStatutLabel(loc.statut)}
                    </span>
                  </div>

                  <div className="location-card-grid">
                    <div className="location-info">
                      <span className="location-info-label"><User size={12} /> Patient</span>
                      <span className="location-info-value">{loc.patientReference} ({loc.patientInitiales})</span>
                    </div>
                    <div className="location-info">
                      <span className="location-info-label"><MapPin size={12} /> Service</span>
                      <span className="location-info-value">{loc.service}</span>
                    </div>
                    <div className="location-info">
                      <span className="location-info-label"><Calendar size={12} /> Début</span>
                      <span className="location-info-value">{formatDate(loc.dateDebut)}</span>
                    </div>
                    {isActive ? (
                      <div className="location-info">
                        <span className="location-info-label"><Calendar size={12} /> Prochaine période</span>
                        <span className="location-info-value">{formatDate(loc.prochainePeriode)}</span>
                      </div>
                    ) : (
                      <div className="location-info">
                        <span className="location-info-label"><Calendar size={12} /> Fin</span>
                        <span className="location-info-value">{formatDate(loc.dateFin)}</span>
                      </div>
                    )}
                    <div className="location-info">
                      <span className="location-info-label">Durée écoulée</span>
                      <span className="location-info-value">{loc.duréeMois} mois</span>
                    </div>
                  </div>

                  {!isActive && loc.motifArret && (
                    <div className="location-motif">
                      <Info size={14} /> Motif d'arrêt : <strong>{loc.motifArret}</strong>
                    </div>
                  )}
                </div>

                {isActive && (
                  <div className="location-card-actions">
                    <button className="btn-stop" onClick={() => ouvrirArret(loc)}>
                      <StopCircle size={16} /> Arrêter la location
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Modal d'arrêt */}
      {stopModalLoc && (
        <div className="modal-overlay" onClick={() => setStopModalLoc(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-header-icon modal-header-icon-warning">
                <StopCircle size={24} />
              </div>
              <div>
                <h2>Arrêter la location</h2>
                <p>{stopModalLoc.materielNom} — {stopModalLoc.id}</p>
              </div>
              <button className="btn-icon-sm modal-close" onClick={() => setStopModalLoc(null)}>
                <X size={18} />
              </button>
            </div>

            <div className="modal-body">
              <div className="modal-info">
                <Info size={16} />
                <p>
                  Un technicien RESPIDOM passera récupérer le matériel à l'adresse indiquée.
                  Vous recevrez par mail un récapitulatif détaillé.
                </p>
              </div>

              <div className="form-group">
                <label>Date d'arrêt souhaitée <span className="required">*</span></label>
                <input
                  type="date"
                  value={dateArret}
                  onChange={(e) => setDateArret(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>

              <div className="form-group">
                <label>Motif d'arrêt <span className="required">*</span></label>
                <select value={motifArret} onChange={(e) => setMotifArret(e.target.value)}>
                  <option value="">-- Sélectionner un motif --</option>
                  <option value="Sortie d'hospitalisation">Sortie d'hospitalisation</option>
                  <option value="Patient autonome - retour domicile">Patient autonome - retour domicile</option>
                  <option value="Changement de matériel">Changement de matériel</option>
                  <option value="Fin de prescription médicale">Fin de prescription médicale</option>
                  <option value="Décès du patient">Décès du patient</option>
                  <option value="Transfert en autre établissement">Transfert en autre établissement</option>
                  <option value="Autre">Autre motif</option>
                </select>
              </div>

            </div>

            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setStopModalLoc(null)}>
                Annuler
              </button>
              <button
                className="btn-danger"
                onClick={confirmerArret}
                disabled={!motifArret || !dateArret}
              >
                <StopCircle size={16} /> Confirmer l'arrêt
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Locations;
